/**
 * Text Studio — shared overlay renderer.
 *
 * Single source of truth for how text overlays are drawn, used by BOTH the
 * live CanvasPreview and the export encoder so what you see is exactly what
 * exports. Supports Instagram-style pills, strokes, glows, gradient fills,
 * alignment, rotation, multi-line text, and entrance animations.
 *
 * Overlay schema (all fields optional beyond id/text — defaults below):
 * {
 *   id, text,                       // '\n' for multi-line
 *   fontFamily: 'inter',            // key of FONT_DEFS
 *   fontSize: 32,                   // px relative to a 1080-wide frame (scaled)
 *   color: '#ffffff',
 *   gradient: null | { from, to }, // gradient text fill (overrides color)
 *   align: 'center',                // left | center | right
 *   bg: null | { color, opacity, radius, paddingX, paddingY },
 *   stroke: null | { color, width },
 *   shadow: null | { color, blur, x, y },
 *   opacity: 1, rotation: 0, letterSpacing: 0,
 *   x: 0.5, y: 0.5,                 // normalized center position
 *   startTime: 0, endTime: 5000,    // ms on the timeline
 *   animation: 'none',              // none | fade | pop | slideUp | typewriter
 *   animDurationMs: 400,
 *   preset: 'classic',
 * }
 */

// ── Fonts (all free / open-source — Google Fonts) ───────────────────────────

export const FONT_DEFS = {
  inter:    { name: 'Clean',       family: '"DM Sans", sans-serif',            weight: 600, gf: 'DM+Sans:wght@600;700' },
  anton:    { name: 'Meme',        family: '"Anton", Impact, sans-serif',      weight: 400, gf: 'Anton' },
  bebas:    { name: 'Headline',    family: '"Bebas Neue", sans-serif',         weight: 400, gf: 'Bebas+Neue' },
  pacifico: { name: 'Script',      family: '"Pacifico", cursive',              weight: 400, gf: 'Pacifico' },
  caveat:   { name: 'Handwritten', family: '"Caveat", cursive',                weight: 600, gf: 'Caveat:wght@600' },
  playfair: { name: 'Elegant',     family: '"Playfair Display", serif',        weight: 600, gf: 'Playfair+Display:wght@600' },
  marker:   { name: 'Marker',      family: '"Permanent Marker", cursive',      weight: 400, gf: 'Permanent+Marker' },
  mono:     { name: 'Typewriter',  family: '"JetBrains Mono", monospace',      weight: 500, gf: 'JetBrains+Mono:wght@500' },
};

let fontsInjected = false;
/** Injects the Google Fonts stylesheet once and resolves when fonts are usable. */
export const ensureFontsLoaded = async () => {
  if (!fontsInjected) {
    fontsInjected = true;
    const families = Object.values(FONT_DEFS).map((f) => `family=${f.gf}`).join('&');
    const link = document.createElement('link');
    link.rel = 'stylesheet';
    link.href = `https://fonts.googleapis.com/css2?${families}&display=swap`;
    document.head.appendChild(link);
  }
  try {
    await Promise.all(
      Object.values(FONT_DEFS).map((f) => document.fonts.load(`${f.weight} 32px ${f.family}`)),
    );
  } catch { /* canvas falls back to system fonts */ }
};

// ── One-tap style presets (Instagram-parity and beyond) ─────────────────────

export const TEXT_PRESETS = [
  { id: 'classic',    name: 'Classic',    overlay: { fontFamily: 'inter',    color: '#ffffff', gradient: null, bg: null, stroke: null, shadow: { color: 'rgba(0,0,0,0.6)', blur: 8, x: 0, y: 2 }, animation: 'fade' } },
  { id: 'meme',       name: 'Meme',       overlay: { fontFamily: 'anton',    color: '#ffffff', gradient: null, bg: null, stroke: { color: '#000000', width: 6 }, shadow: null, animation: 'pop' } },
  { id: 'pill',       name: 'Pill',       overlay: { fontFamily: 'inter',    color: '#111111', gradient: null, bg: { color: '#ffffff', opacity: 0.95, radius: 14, paddingX: 18, paddingY: 10 }, stroke: null, shadow: { color: 'rgba(0,0,0,0.25)', blur: 12, x: 0, y: 4 }, animation: 'slideUp' } },
  { id: 'neon',       name: 'Neon',       overlay: { fontFamily: 'bebas',    color: '#f0abfc', gradient: null, bg: null, stroke: null, shadow: { color: '#d946ef', blur: 24, x: 0, y: 0 }, animation: 'fade' } },
  { id: 'gradient',   name: 'Gradient',   overlay: { fontFamily: 'anton',    color: '#ffffff', gradient: { from: '#f43f7b', to: '#a855f7' }, bg: null, stroke: null, shadow: { color: 'rgba(0,0,0,0.4)', blur: 10, x: 0, y: 3 }, animation: 'pop' } },
  { id: 'headline',   name: 'Headline',   overlay: { fontFamily: 'bebas',    color: '#ffffff', gradient: null, bg: null, stroke: null, shadow: { color: 'rgba(0,0,0,0.5)', blur: 6, x: 0, y: 2 }, letterSpacing: 4, animation: 'slideUp' } },
  { id: 'script',     name: 'Script',     overlay: { fontFamily: 'pacifico', color: '#ffffff', gradient: null, bg: null, stroke: null, shadow: { color: 'rgba(0,0,0,0.5)', blur: 10, x: 0, y: 2 }, animation: 'fade' } },
  { id: 'typewriter', name: 'Typewriter', overlay: { fontFamily: 'mono',     color: '#a7f3d0', gradient: null, bg: { color: '#000000', opacity: 0.65, radius: 6, paddingX: 12, paddingY: 8 }, stroke: null, shadow: null, animation: 'typewriter' } },
];

export const OVERLAY_DEFAULTS = {
  fontFamily: 'inter',
  fontSize: 32,
  color: '#ffffff',
  gradient: null,
  align: 'center',
  bg: null,
  stroke: null,
  shadow: { color: 'rgba(0,0,0,0.6)', blur: 8, x: 0, y: 2 },
  opacity: 1,
  rotation: 0,
  letterSpacing: 0,
  animation: 'fade',
  animDurationMs: 400,
  preset: 'classic',
};

// ── Animation progress ──────────────────────────────────────────────────────

const easeOutBack = (t) => 1 + 2.70158 * Math.pow(t - 1, 3) + 1.70158 * Math.pow(t - 1, 2);
const easeOut = (t) => 1 - Math.pow(1 - t, 3);

/** Returns {alpha, scale, dy, chars} for the entrance animation at time tMs. */
const animState = (overlay, tMs) => {
  const dur = overlay.animDurationMs || 400;
  const t = Math.max(0, Math.min(1, (tMs - (overlay.startTime || 0)) / dur));
  switch (overlay.animation) {
    case 'fade':       return { alpha: easeOut(t), scale: 1, dy: 0, chars: Infinity };
    case 'pop':        return { alpha: Math.min(1, t * 2), scale: 0.5 + 0.5 * easeOutBack(t), dy: 0, chars: Infinity };
    case 'slideUp':    return { alpha: easeOut(t), scale: 1, dy: (1 - easeOut(t)) * 40, chars: Infinity };
    case 'typewriter': return { alpha: 1, scale: 1, dy: 0, chars: t }; // chars as fraction
    default:           return { alpha: 1, scale: 1, dy: 0, chars: Infinity };
  }
};

// ── Word wrap (opt-in) ──────────────────────────────────────────────────────

/** Fraction of the frame width text wraps within — leaves a safe margin each side. */
const WRAP_MARGIN = 0.9;

/**
 * Greedy word-wrap `text` to `maxWidth` using the ctx's CURRENT font (set the font first).
 * Preserves explicit '\n' paragraph breaks and hard-breaks any single word wider than the frame.
 */
const wrapLines = (ctx, text, maxWidth) => {
  const result = [];
  for (const para of String(text).split('\n')) {
    let line = '';
    for (let word of para.split(' ')) {
      // Hard-break a single word that is itself wider than the frame.
      while (ctx.measureText(word).width > maxWidth && word.length > 1) {
        let cut = word.length;
        while (cut > 1 && ctx.measureText(word.slice(0, cut)).width > maxWidth) cut--;
        if (line) { result.push(line); line = ''; }
        result.push(word.slice(0, cut));
        word = word.slice(cut);
      }
      const test = line ? `${line} ${word}` : word;
      if (!line || ctx.measureText(test).width <= maxWidth) {
        line = test;
      } else {
        result.push(line);
        line = word;
      }
    }
    result.push(line);
  }
  return result.length ? result : [''];
};

// ── Main draw ───────────────────────────────────────────────────────────────

/**
 * Draws one overlay.
 * @param ctx    2D canvas context
 * @param overlay overlay object (see schema)
 * @param box    {x, y, w, h} — the region of the canvas the video occupies
 * @param tMs    current timeline time in ms (drives animations)
 */
export const drawTextOverlay = (ctx, overlay, box, tMs) => {
  const o = { ...OVERLAY_DEFAULTS, ...overlay };
  const font = FONT_DEFS[o.fontFamily] || FONT_DEFS.inter;
  const { alpha, scale, dy, chars } = animState(o, tMs);
  if (alpha <= 0) return;

  // Font size scales with the rendered frame width (authored against 1080)
  const sizeScale = box.w / 1080;
  const fontSize = Math.max(10, o.fontSize * 2.2 * sizeScale);

  // Typewriter: reveal a fraction of characters (whole text incl. newlines)
  let text = o.text || '';
  if (chars !== Infinity) {
    text = text.slice(0, Math.ceil(text.length * chars));
    if (!text) return;
  }
  ctx.save();
  ctx.globalAlpha = alpha * (o.opacity ?? 1);
  ctx.font = `${font.weight} ${fontSize}px ${font.family}`;
  ctx.textBaseline = 'middle';
  if (o.letterSpacing && 'letterSpacing' in ctx) ctx.letterSpacing = `${o.letterSpacing * sizeScale}px`;

  // Wrap to the frame width when the overlay opts in (Story Studio) so long text never overflows.
  // The video editor doesn't set `wrap`, so its explicit-newline behaviour is unchanged.
  const lines = o.wrap ? wrapLines(ctx, text, box.w * WRAP_MARGIN) : text.split('\n');

  const lineHeight = fontSize * 1.25;
  const blockH = lines.length * lineHeight;
  const widths = lines.map((l) => ctx.measureText(l).width);
  const blockW = Math.max(...widths, 1);

  const cx = box.x + o.x * box.w;
  const cy = box.y + o.y * box.h + dy * sizeScale;

  ctx.translate(cx, cy);
  if (o.rotation) ctx.rotate((o.rotation * Math.PI) / 180);
  if (scale !== 1) ctx.scale(scale, scale);

  // Background pill (per line, Instagram-style)
  if (o.bg) {
    const padX = (o.bg.paddingX ?? 16) * sizeScale;
    const padY = (o.bg.paddingY ?? 8) * sizeScale;
    const radius = (o.bg.radius ?? 12) * sizeScale;
    ctx.save();
    ctx.globalAlpha = alpha * (o.opacity ?? 1) * (o.bg.opacity ?? 1);
    ctx.fillStyle = o.bg.color || '#ffffff';
    lines.forEach((line, i) => {
      const w = widths[i];
      if (!line.trim()) return;
      const lx = o.align === 'left' ? -blockW / 2 : o.align === 'right' ? blockW / 2 - w : -w / 2;
      const ly = -blockH / 2 + i * lineHeight + lineHeight / 2;
      const rx = lx - padX;
      const ry = ly - lineHeight / 2 - padY + lineHeight * 0.08;
      const rw = w + padX * 2;
      const rh = lineHeight + padY * 2 - lineHeight * 0.16;
      ctx.beginPath();
      ctx.roundRect(rx, ry, rw, rh, radius);
      ctx.fill();
    });
    ctx.restore();
  }

  // Shadow / glow
  if (o.shadow) {
    ctx.shadowColor = o.shadow.color || 'rgba(0,0,0,0.6)';
    ctx.shadowBlur = (o.shadow.blur ?? 8) * sizeScale;
    ctx.shadowOffsetX = (o.shadow.x ?? 0) * sizeScale;
    ctx.shadowOffsetY = (o.shadow.y ?? 2) * sizeScale;
  }

  // Fill style — solid or gradient across the text block
  if (o.gradient) {
    const g = ctx.createLinearGradient(-blockW / 2, 0, blockW / 2, 0);
    g.addColorStop(0, o.gradient.from);
    g.addColorStop(1, o.gradient.to);
    ctx.fillStyle = g;
  } else {
    ctx.fillStyle = o.color;
  }

  lines.forEach((line, i) => {
    const w = widths[i];
    const lx = o.align === 'left' ? -blockW / 2 : o.align === 'right' ? blockW / 2 - w : -w / 2;
    const ly = -blockH / 2 + i * lineHeight + lineHeight / 2;
    if (o.stroke && o.stroke.width > 0) {
      ctx.lineJoin = 'round';
      ctx.strokeStyle = o.stroke.color || '#000000';
      ctx.lineWidth = o.stroke.width * sizeScale;
      ctx.strokeText(line, lx, ly);
    }
    ctx.fillText(line, lx, ly);
  });

  ctx.restore();
};

/**
 * Shared overlay geometry — the single source of truth for the center, block
 * size, padding, size scale and rotation of an overlay. Used by hit-testing,
 * the selection outline and the resize/rotate handles so preview == export and
 * every consumer agrees on where the box and handles are.
 *
 * @returns {{ cx, cy, blockW, blockH, pad, sizeScale, rotationRad }}
 *   cx, cy       — canvas-pixel center of the overlay
 *   blockW/blockH — unrotated text block width/height in canvas px
 *   pad          — generous padding (touch target) around the block
 *   sizeScale    — box.w / 1080 (authoring scale)
 *   rotationRad  — overlay rotation in radians
 */
export const measureOverlay = (ctx, overlay, box) => {
  const o = { ...OVERLAY_DEFAULTS, ...overlay };
  const font = FONT_DEFS[o.fontFamily] || FONT_DEFS.inter;
  const sizeScale = box.w / 1080;
  const fontSize = Math.max(10, o.fontSize * 2.2 * sizeScale);
  ctx.save();
  ctx.font = `${font.weight} ${fontSize}px ${font.family}`;
  const lines = o.wrap ? wrapLines(ctx, o.text || '', box.w * WRAP_MARGIN) : (o.text || '').split('\n');
  const lineHeight = fontSize * 1.25;
  const blockH = lines.length * lineHeight;
  const blockW = Math.max(...lines.map((l) => ctx.measureText(l).width), 1);
  ctx.restore();
  return {
    cx: box.x + o.x * box.w,
    cy: box.y + o.y * box.h,
    blockW,
    blockH,
    pad: 20 * sizeScale, // generous touch target
    sizeScale,
    rotationRad: ((o.rotation || 0) * Math.PI) / 180,
  };
};

/**
 * Maps a point given in an overlay's LOCAL frame (unrotated, origin at the
 * overlay center) to absolute canvas coords, applying the overlay's rotation.
 * @returns {{ x, y }}
 */
export const rotatePoint = (cx, cy, localX, localY, rotationRad) => {
  const cos = Math.cos(rotationRad);
  const sin = Math.sin(rotationRad);
  return {
    x: cx + localX * cos - localY * sin,
    y: cy + localX * sin + localY * cos,
  };
};

/**
 * Hit-test a point (canvas px) against an overlay's bounding box.
 * Returns true if (px, py) falls inside the overlay's drawn area (rotation-aware).
 */
export const hitTestOverlay = (ctx, overlay, box, px, py) => {
  const { cx, cy, blockW, blockH, pad, rotationRad } = measureOverlay(ctx, overlay, box);
  // Transform the point into the overlay's local (unrotated) frame, then test
  // against the axis-aligned padded block.
  const cos = Math.cos(-rotationRad);
  const sin = Math.sin(-rotationRad);
  const dx = px - cx;
  const dy = py - cy;
  const lx = dx * cos - dy * sin;
  const ly = dx * sin + dy * cos;
  return (
    lx >= -blockW / 2 - pad && lx <= blockW / 2 + pad &&
    ly >= -blockH / 2 - pad && ly <= blockH / 2 + pad
  );
};
